import i18next from "i18next";

import { observeServerDate } from "../lib/serverClock";

/**
 * HTTP-клиент к backend'у.
 *
 * Access-токен живёт только в памяти вкладки: в localStorage его класть нельзя,
 * оттуда его достанет любой XSS. Долгоживущий refresh-токен браузер хранит сам
 * в httpOnly-cookie, недоступной из JavaScript, поэтому все запросы идут
 * с `credentials: "include"`.
 */

export const API_URL = (
  import.meta.env.VITE_API_URL ?? "http://localhost:8080"
).replace(/\/$/, "");

/** Ошибка API в том виде, в каком её отдаёт gRPC-Gateway. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  /** Требуется вход или сессия истекла. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Колбэк вызывается, когда сессию восстановить не удалось. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | string[] | undefined | null>;
  /** Внутренний флаг: не пытаться обновлять токен повторно. */
  skipRefresh?: boolean;
}

/**
 * Обновление access-токена.
 *
 * Промис общий: refresh-токен одноразовый и при каждом обмене ротируется, поэтому
 * два параллельных запроса отозвали бы токен друг у друга и разлогинили бы
 * пользователя. Все, кому нужен свежий токен, ждут один и тот же вызов.
 */
let refreshPromise: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (!response.ok) return false;

      const data = (await response.json()) as { accessToken?: string };
      if (!data.accessToken) return false;

      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Сбрасываем в микротаске, чтобы ожидающие успели прочитать результат.
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${API_URL}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, item);
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, skipRefresh = false } = options;

  const headers: Record<string, string> = {
    // Язык читателя: по нему сервер выбирает версию названия и описания
    // квеста. Угадывать по браузеру нельзя — человек выбрал язык сам,
    // и он может не совпадать с системным.
    "Accept-Language": i18next.resolvedLanguage ?? "en",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const sentAt = Date.now();
  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: "include",
    // Ответ из кэша браузера принёс бы старый заголовок Date, и часы
    // сервера (serverClock) уехали бы на возраст копии.
    cache: "no-store",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  observeServerDate(response.headers.get("Date"), sentAt, Date.now());

  // Токен живёт 15 минут; вместо того чтобы показывать пользователю ошибку,
  // молча обновляем его по refresh-cookie и повторяем запрос один раз.
  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return request<T>(path, { ...options, skipRefresh: true });
    }
    accessToken = null;
    onUnauthorized?.();
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = "Unknown";
  let message = i18next.t("error.httpStatus", { status: response.status });

  try {
    const data = (await response.json()) as { code?: string; message?: string };
    if (data.code) code = data.code;
    if (data.message) message = data.message;
  } catch {
    // Тело не JSON — оставляем сообщение по умолчанию.
  }

  return new ApiError(response.status, code, message);
}

/** Загрузка файла напрямую в хранилище по presigned-ссылке, минуя backend. */
export async function uploadToStorage(
  uploadUrl: string,
  file: File,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    // Content-Type входит в подпись ссылки: заголовок обязан совпадать
    // с тем, который был указан при её запросе.
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      "UploadFailed",
      i18next.t("error.upload"),
    );
  }
}
